// @flow
import { createAction } from 'redux-actions';
import type { ActionType, Thunk } from 'types/redux';
import * as actionTypes from './actionTypes';

import { format as formatDate } from 'date-fns';
import {
  promotionsWS,
  anonymousPromotionWS,
  type Promotion
} from 'api/xws/promotions/promotionsWs';

// Actions
import { setDefaultPromotion } from 'store/reducers/marketing/actions';
import { apptNotModified } from 'store/reducers/userInteractions/actions';
// Selectors
import { getQueryParam, getWebkey } from 'store/reducers/queryParams/selectors';
import { getProperty } from 'store/reducers/user/personInfo/selectors';
import { getTokenId } from 'store/reducers/tokenId/selectors';
import { getDefaultPromo } from 'store/reducers/marketing/selectors';
import { getContactInfo } from 'store/reducers/appointment/contactInfo/selectors';
import {
  hasPromotion,
  getActivePromotion
} from 'store/reducers/promotions/selectors';

// Utils
import { clearTime } from 'utils/date';

export const getFirstPromotion = (
  vin: string,
  promoCode: string
): Thunk<Promise<Promotion | null>> => async (dispatch, getState) => {
  const extId =
    getProperty(getState(), 'extId') || getQueryParam('extId')(getState());
  if (vin && extId) {
    try {
      const promoList = await dispatch(getPromoList(vin, promoCode));
      const promoListFiltered =
        promoList.filter(promo => {
          return (
            clearTime(new Date(promo.apptEndDate)) >= clearTime(new Date())
          );
        }) || [];
      const sortCallBack = (a, b) => {
        if (a.promoPriority === b.promoPriority) {
          return a.startDate > b.startDate ? 1 : -1;
        } else {
          return a.promoPriority < b.promoPriority ? 1 : -1;
        }
      };
      promoListFiltered.sort(sortCallBack);
      return promoListFiltered[0] || null;
    } catch (e) {
      return null;
    }
  }
  return null;
};

export const getPromoList = (
  vin: string,
  promoCode: string
): Thunk<Promise<any>> => async (dispatch, getState) => {
  const extId = String(
    getProperty(getState(), 'extId') ||
      getQueryParam('extId')(getState()) ||
      getContactInfo(getState()).extId
  );
  if (vin && extId) {
    try {
      const webkey = getWebkey(getState());
      const tokenId = getTokenId(getState());
      const {
        data: {
          promotions: { promotionList }
        }
      } = await promotionsWS(
        {
          date: formatDate(new Date(), 'YYYY-MM-DD') + '',
          promoCode: promoCode || getQueryParam('promocode')(getState()),
          tokenId: tokenId + ''
        },
        { webkey, extId, vin }
      );
      return promotionList;
    } catch (e) {
      return [];
    }
  }
};

// TODO: refactor every place where it is used, this method should return promo
//  without set it in redux
export const getAnonymousPromotion = (
  promoCode?: string
): Thunk<Promise<any>> => async (dispatch, getState) => {
  const webkey = getWebkey(getState());
  const body = promoCode ? { promotionCode: promoCode } : null;
  const isDefaultPromoSet = getDefaultPromo(getState());
  if (!isDefaultPromoSet) {
    try {
      const {
        data: { object: promo }
      } = await anonymousPromotionWS(body, { webkey });
      if (promo) {
        dispatch(setActivePromotion(promo));
        dispatch(setDefaultPromotion(true));
        dispatch(apptNotModified());
        return promo;
      } else {
        return null;
      }
    } catch (e) {
      return null;
    }
  } else {
    return hasPromotion ? getActivePromotion(getState()) : null;
  }
};

export const setActivePromotion = createAction(
  actionTypes.SET_PROMOTION,
  (activePromotion: Promotion | null) => activePromotion
);

export const resetPromotion = createAction(actionTypes.RESET_PROMOTION);

const actions = {
  setActivePromotion,
  resetPromotion
};

export type Action = ActionType<$Values<typeof actions>>;



// WEBPACK FOOTER //
// ./src/store/reducers/promotions/actions.js